import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  initialDraft,
  type AccessControlState,
  type AccessDraft,
  type CandidateRequest,
} from './AccessControlModel';
import { limitForNumber, validateLimit } from './useFormValidation';

export interface AccessDraftApi {
  draft: AccessDraft;
  /** Marca/desmarca uma tool. */
  alternarTool: (toolId: string) => void;
  /** Escreve o limite de um parâmetro (texto, como o usuário digitou). */
  definirLimite: (toolId: string, parametro: string, valor: string) => void;
  /** Marca/desmarca uma identidade invocável (A2A de saída). */
  alternarA2A: (resourceId: string) => void;
  descartar: () => void;
  /** Quantas mudanças em relação ao contrato vigente (FR-077). */
  mudancas: number;
  /** Erros de limite por `${toolId}:${parametro}` (FR-073). */
  erros: Record<string, string>;
  /** Corpo de `submitAccessControlCandidate`. */
  paraEnvio: () => CandidateRequest;
}

/**
 * Rascunho local de T14 (FR-077): tudo é editado na memória e comparado com o
 * contrato vigente. Nada vai ao servidor até **Salvar → enviar para aprovação**.
 */
export function useAccessControlDraft(estado: AccessControlState | undefined): AccessDraftApi {
  const [draft, setDraft] = useState<AccessDraft>(() =>
    estado ? initialDraft(estado) : { tools: {}, a2a: {} },
  );

  // Estado novo (outra identidade, ou recarga) recomeça o rascunho.
  useEffect(() => {
    setDraft(estado ? initialDraft(estado) : { tools: {}, a2a: {} });
  }, [estado]);

  const alternarTool = useCallback((toolId: string) => {
    setDraft((current) => {
      const tool = current.tools[toolId];
      if (!tool) return current;
      return {
        ...current,
        tools: { ...current.tools, [toolId]: { ...tool, concedida: !tool.concedida } },
      };
    });
  }, []);

  const defineLimit = useCallback((toolId: string, parametro: string, valor: string) => {
    setDraft((current) => {
      const tool = current.tools[toolId];
      if (!tool) return current;
      return {
        ...current,
        tools: {
          ...current.tools,
          [toolId]: { ...tool, limites: { ...tool.limites, [parametro]: valor } },
        },
      };
    });
  }, []);

  const alternarA2A = useCallback((resourceId: string) => {
    setDraft((current) => ({
      ...current,
      a2a: { ...current.a2a, [resourceId]: !current.a2a[resourceId] },
    }));
  }, []);

  const descartar = useCallback(() => {
    setDraft(estado ? initialDraft(estado) : { tools: {}, a2a: {} });
  }, [estado]);

  /**
   * Uma mudança é: tool marcada/desmarcada, ou limite diferente do vigente.
   * Limite inválido conta como mudança — o rodapé bloqueia pelo erro, não por
   * fingir que nada mudou.
   */
  const mudancas = useMemo(() => {
    if (!estado) return 0;
    let total = 0;

    estado.tools.forEach((tool) => {
      const current = draft.tools[tool.tool_id];
      if (!current) return;
      if (current.concedida !== tool.granted) total += 1;
      tool.constrainable.forEach((parametro) => {
        const before = tool.constraints?.[parametro];
        const after = limitForNumber(current.limites[parametro] ?? '');
        if (before !== after) total += 1;
      });
    });

    estado.a2a_out.forEach((alvo) => {
      if ((draft.a2a[alvo.resource_id] ?? false) !== alvo.granted) total += 1;
    });

    return total;
  }, [draft, estado]);

  const erros = useMemo(() => {
    const foundItems: Record<string, string> = {};
    if (!estado) return foundItems;
    estado.tools.forEach((tool) => {
      const current = draft.tools[tool.tool_id];
      if (!current?.concedida) return;
      tool.constrainable.forEach((parametro) => {
        const erro = validateLimit(current.limites[parametro] ?? '');
        if (erro) foundItems[`${tool.tool_id}:${parametro}`] = erro;
      });
    });
    return foundItems;
  }, [draft, estado]);

  /**
   * O corpo do candidato — **uma** lista, duas espécies (DEF-29, 09/09).
   *
   * As delegações viajam em `capabilities[]` com `kind: agent` e
   * `target_resource_id` (o recurso agente, fase 2); o `a2a[]` do contrato ficou deprecado e a tela **não o
   * envia mais**. Mandar nas duas portas não duplicaria o alvo do lado de lá,
   * mas deixaria duas origens para a mesma informação — que é exatamente o que
   * o DEF-29 desfez.
   *
   * `kind` vai explícito também na tool. Ele é opcional no contrato (ausente lê
   * como `tool`), e omiti-lo funcionaria; num corpo que agora carrega duas
   * espécies, a espécie de cada linha é dado, não default.
   *
   * Delegação não leva `constraints`: um limite ali é `422 delegationNoLimit` —
   * `agents[]` do documento do PDP é uma lista de slugs, sem onde aplicá-lo.
   */
  const forSend = useCallback((): CandidateRequest => {
    const tools = (estado?.tools ?? [])
      .filter((tool) => draft.tools[tool.tool_id]?.concedida)
      .map((tool) => {
        const limits = draft.tools[tool.tool_id]?.limites ?? {};
        const constraints: Record<string, number> = {};
        tool.constrainable.forEach((parametro) => {
          const num = limitForNumber(limits[parametro] ?? '');
          if (num !== undefined) constraints[parametro] = num;
        });
        return {
          kind: 'tool' as const,
          resource_id: tool.resource_id,
          tool_id: tool.tool_id,
          constraints,
        };
      });

    const delegations = (estado?.a2a_out ?? [])
      .filter((target) => draft.a2a[target.resource_id])
      .map((target) => ({
        kind: 'agent' as const,
        target_resource_id: target.resource_id,
        constraints: {},
      }));

    return {
      base_contract_version: estado?.contract_version ?? 0,
      capabilities: [...tools, ...delegations],
    };
  }, [draft, estado]);

  return { draft, alternarTool, definirLimite: defineLimit, alternarA2A, descartar, mudancas, erros, paraEnvio: forSend };
}
